import { formatRelativeTime } from "./siataWeatherFormatting";
import { formatRadarTimestamp } from "./radarTimestamp";

/** @typedef {'fresh'|'stale'|'down'|'experimental'|'unknown'|'not_configured'|'disabled'} HealthStatus */

export const FRESHNESS_LABELS = {
  fresh: "Updated recently",
  stale: "May be outdated",
  down: "Currently unavailable",
  experimental: "Still being validated",
  unknown: "Freshness unknown",
  not_configured: "Not configured",
  disabled: "Turned off",
  historical: "Historical archive",
  no_data: "No data yet",
};

export function getFreshnessLabel(healthStatus) {
  const key = String(healthStatus || "unknown").toLowerCase();
  return FRESHNESS_LABELS[key] || FRESHNESS_LABELS.unknown;
}

export function getFreshnessDetail({ healthStatus, observedAt, fallbackActive = false, backupSourceLabel = "RainViewer" }) {
  const status = String(healthStatus || "unknown").toLowerCase();
  const relative = observedAt ? formatRelativeTime(observedAt) : null;
  const absolute = observedAt ? formatRadarTimestamp(observedAt) : null;

  if (fallbackActive) {
    return `Official source unavailable. Showing ${backupSourceLabel} backup.`;
  }

  if (status === "fresh" && relative) {
    return `Updated ${relative}.`;
  }

  if (status === "stale") {
    if (absolute) {
      return `Last update ${absolute}. Data may be outdated.`;
    }
    return "Data may be outdated.";
  }

  if (status === "down") {
    return "This source is not available right now.";
  }

  if (status === "experimental") {
    return "This layer is official but still being validated.";
  }

  if (status === "unknown") {
    return "Freshness cannot be confirmed, so confidence is reduced.";
  }

  if (status === "not_configured") {
    return "This layer is not set up on the server yet.";
  }

  if (relative) {
    return `Last observed ${relative}.`;
  }

  return null;
}

export function getFreshnessTone(healthStatus, { error = false, fallbackActive = false } = {}) {
  if (error || String(healthStatus).toLowerCase() === "down") {
    return "error";
  }
  if (fallbackActive || String(healthStatus).toLowerCase() === "stale") {
    return "warning";
  }
  if (String(healthStatus).toLowerCase() === "fresh") {
    return "ok";
  }
  return "muted";
}

/**
 * Public-friendly status message (replaces technical health codes in UI).
 */
export function buildPublicSourceStatusMessage({
  healthStatus,
  observedAt,
  error,
  warning,
  fallbackActive,
  backupSourceLabel,
  cached,
}) {
  if (error) {
    return {
      tone: "error",
      label: getFreshnessLabel("down"),
      message: error,
      detail: warning || null,
    };
  }

  if (cached) {
    return {
      tone: "warning",
      label: "Cached data",
      message: "Showing the most recent cached imagery.",
      detail: warning || null,
    };
  }

  const label = fallbackActive ? "Showing backup source" : getFreshnessLabel(healthStatus);
  const detail =
    getFreshnessDetail({ healthStatus, observedAt, fallbackActive, backupSourceLabel }) ||
    warning ||
    null;

  return {
    tone: getFreshnessTone(healthStatus, { error: Boolean(error), fallbackActive }),
    label,
    message: detail || label,
    detail: warning && detail !== warning ? warning : null,
  };
}

export function resolveIdeamExplainerKey(productIdentityStatus) {
  switch (productIdentityStatus) {
    case "confirmed_radar_composite":
      return "ideam_radar_composite";
    case "official_precipitation_intensity":
      return "ideam_precipitation_intensity";
    default:
      return "ideam_unknown";
  }
}
